import { motion } from "framer-motion";
import { Youtube, Instagram, Send, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/constants/brand";

const cards = [
  {
    key: "youtube",
    label: "YouTube",
    handle: "@buggucapital",
    description: "In-depth market breakdowns, tutorials and weekly setups.",
    icon: Youtube,
    href: BRAND.socials.youtube,
    ring: "hsl(0 90% 55%)",
    ringSoft: "hsl(0 90% 55% / 0.18)",
    cta: "Subscribe",
  },
  {
    key: "instagram",
    label: "Instagram",
    handle: "@buggucapital",
    description: "Bite-sized daily insights, reels & chart snapshots.",
    icon: Instagram,
    href: BRAND.socials.instagram,
    ring: "hsl(320 85% 55%)",
    ringSoft: "hsl(320 85% 55% / 0.18)",
    cta: "Follow",
  },
  {
    key: "telegram",
    label: "Telegram",
    handle: "Buggu Capital Community",
    description: "Real-time updates, community discussion & alerts.",
    icon: Send,
    href: BRAND.socials.telegram,
    ring: "hsl(199 89% 55%)",
    ringSoft: "hsl(199 89% 55% / 0.18)",
    cta: "Join Channel",
  },
];

const SocialConnect = () => {
  return (
    <section id="connect" className="section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip" data-testid="connect-badge">Join the community</span>
          <h2
            className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
            data-testid="connect-heading"
          >
            Learn together. <span className="text-gradient-brand">Grow together.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground md:text-lg">
            Follow along on your favorite platform — pick the format that fits your day.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {cards.map((c, idx) => {
            const Icon = c.icon;
            return (
              <motion.a
                key={c.key}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="group relative overflow-hidden rounded-3xl border border-border bg-card/50 p-7 lift"
                data-testid={`social-card-${c.key}`}
              >
                <div
                  className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full opacity-40 blur-3xl transition-all duration-500 group-hover:scale-125 group-hover:opacity-70"
                  style={{ background: c.ring }}
                  aria-hidden="true"
                />

                <div
                  className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl"
                  style={{ background: c.ringSoft, color: c.ring }}
                >
                  <Icon size={26} strokeWidth={2} />
                </div>

                <div className="relative mt-6">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {c.label}
                  </p>
                  <p className="mt-1 font-display text-xl font-bold">{c.handle}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {c.description}
                  </p>
                </div>

                <div className="relative mt-6 flex items-center justify-between">
                  <span
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all"
                    style={{ background: c.ringSoft, color: c.ring }}
                  >
                    {c.cta}
                  </span>
                  <ArrowUpRight
                    size={22}
                    className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                  />
                </div>
              </motion.a>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          Your journey to <span className="font-semibold text-foreground">financial freedom</span> starts with one click.
        </p>
      </div>
    </section>
  );
};

export default SocialConnect;
